<template>
  <settings-modal
    class="t-refund-esim-fee-modal"
  >
    <v-progress-circular
      v-if="payment == null"
      class="refund-esim-fee--modal__loader"
      indeterminate
      color="primary"
    />
    <template v-else>
      <template slot="heading">Refund E-Sim fee</template>

      <v-layout
        row
        style="font-size: 16px"
      >
        <v-flex xs4>Sender:</v-flex>
        <v-flex
          class="refund-esim-fee--modal__sender"
          xs8
        >{{ `${payment.recipientProfileName} (${payment.recipientAccountId})` }}</v-flex>
      </v-layout>

      <v-layout
        row
        style="font-size: 16px"
      >
        <v-flex xs4>Recipient:</v-flex>
        <v-flex
          class="refund-esim-fee--modal__recipient"
          xs8
        >{{ `${payment.senderProfileName} (${payment.senderAccountId})` }}</v-flex>
      </v-layout>

      <v-layout
        row
        align-center
      >
        <v-flex xs4>
          <v-text-field
            id="amount"
            v-model="form.senderAmount"
            type="number"
            label="Payment amount"
            placeholder="0.00"
            persistent-hint
            :error-messages="getValidationErrorMessage('amount')"
          />
        </v-flex>

        <v-flex xs2>
          {{ payment.recipientCurrency }}
        </v-flex>
      </v-layout>

      <v-text-field
        v-model="form.comment"
        label="Comment"
        persistent-hint
        :error-messages="getValidationErrorMessage('comment')"
      />

      <error-alert :error="error" />

      <template slot="actions">
        <v-btn
          color="light-blue darken-3"
          class="t-confirm-button"
          flat
          :disabled="loading"
          @click="onCreatePaymentButtonClick"
        >Create payment</v-btn>

        <v-btn
          color="light-blue darken-3"
          class="t-cancel-button"
          flat
          @click="onCancelButtonClick"
        >Cancel</v-btn>
      </template>
    </template>
  </settings-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  // Events
  import { EVENT_PAYMENT_UPDATE } from '@/events/payments'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import EsimApi from '@/services/Api/EsimApi'
  import PaymentApi from '@/services/Api/PaymentApi'

  // Mixins
  import form from '@/mixins/form'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import Payment from '@/models/Payment'

  export default {
    name: 'RefundEsimFeeModal',
    components: {
      SettingsModal,
      ErrorAlert
    },
    mixins: [form, flashMessages],
    data () {
      return {
        refundEsimFeeApi: new ApiRequest(EsimApi.refundEsimFee),
        paymentApiRequest: new ApiRequest(PaymentApi.fetchById, Payment),
        form: {
          paymentId: null,
          senderAmount: null,
          senderCurrency: null,
          senderAccountId: null,
          recipient: null,
          comment: null
        },
        requiredFields: ['comment', 'senderAmount']
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      payment () {
        if (this.esimOrderPaymentId) {
          return this.paymentApiRequest.data
        }

        return this.modal.data.payment
      },
      esimOrderPaymentId () {
        return this.modal.data.esimOrderPaymentId
      },
      currentComment () {
        return `Refund for e-Sim ${this.payment.id} payment`
      },
      loading () {
        return this.refundEsimFeeApi.loading
      },
      error () {
        return this.refundEsimFeeApi.error
      }
    },
    created () {
      this.prepareModalData()
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      async prepareModalData () {
        try {
          if (this.esimOrderPaymentId) {
            await this.paymentApiRequest.execute(this.esimOrderPaymentId)
          }
          this.initFormData()
        } catch {
          this.closeModal()
          this.showErrorFlashMessage('Payment could not be loaded')
        }
      },
      initFormData () {
        const formData = {
          paymentId: this.payment.id,
          senderAmount: this.payment.recipientAmount,
          senderCurrency: this.payment.recipientCurrency,
          senderAccountId: this.payment.recipientAccountId,
          recipient: this.payment.senderAccountId,
          comment: this.currentComment
        }

        this.setFormData(formData)
      },
      onCreatePaymentButtonClick () {
        this.refundEsimFeeApi
          .execute(this.form)
          .then(this.refundEsimFeeSuccess)
          .catch(() => this.showErrorFlashMessage('E-sim fee refund failed'))
      },
      onCancelButtonClick () {
        this.closeModal()
      },
      refundEsimFeeSuccess () {
        this.$eventHub.$emit(EVENT_PAYMENT_UPDATE)
        this.showSuccessFlashMessage(`Refund for e-Sim fee ${this.payment.id} successfully`)
      }
    }
  }
</script>
